(function($) {
    /**
     * @interface OpenSeadragon.FlexRenderer.WebGLImplementation
     * Backend rendering contract used by `FlexRenderer`.
     *
     * Despite the historical name, this interface documents responsibilities that any GPU backend
     * is expected to match, including future WebGPU implementations. The concrete backend owns GPU
     * resources and shader programs. The outer renderer owns normalized inspector state, shader
     * ordering, and the decision of whether inspector behavior stays inline in the second pass or
     * is delegated to a backend-specific compositor path.
     */
    $.FlexRenderer.WebGLImplementation = class {
        /**
         * Create a WebGL rendering implementation.
         * @param {FlexRenderer} renderer owner of this implementation
         * @param {WebGLRenderingContext|WebGL2RenderingContext} gl
         * @param {String} webGLVersion "1.0" or "2.0"
         */
        constructor(renderer, gl, webGLVersion) {
            //todo renderer name is misleading, rename
            this.renderer = renderer;
            this.gl = gl;
            this.webGLVersion = webGLVersion;
        }

        /**
         * Static WebGLRenderingContext creation (to avoid class instantiation in case of missing support).
         * @param {HTMLCanvasElement} canvas
         * @param {string} webGLVersion
         * @param {Object} contextAttributes desired options used for the canvas webgl context creation
         * @return {WebGLRenderingContext|WebGL2RenderingContext}
         */
        static createWebglContext(canvas, webGLVersion, contextAttributes) {
            // indicates that the canvas contains an alpha buffer
            contextAttributes.alpha = true;
            // indicates that the page compositor will assume the drawing buffer contains colors with pre-multiplied alpha
            contextAttributes.premultipliedAlpha = true;
            contextAttributes.preserveDrawingBuffer = true;

            if (webGLVersion === "1.0") {
                return canvas.getContext('webgl', contextAttributes);
            } else {
                return canvas.getContext('webgl2', contextAttributes);
            }
        }

        get firstPassProgramKey() {
            throw("$.FlexRenderer.WebGLImplementation::firstPassProgram must be implemented!");
        }

        get secondPassProgramKey() {
            throw("$.FlexRenderer.WebGLImplementation::secondPassProgram must be implemented!");
        }

        get inspectorCompositorProgramKey() {
            throw("$.FlexRenderer.WebGLImplementation::inspectorCompositorProgram must be implemented!");
        }

        /**
         * Whether this backend can render into a high-precision (float) color target.
         *
         * Backends that only support 8-bit unorm targets keep the default. The renderer uses
         * this to decide whether a requested `precision: "float16"` is honored or downgraded.
         *
         * @return {boolean}
         */
        get supportsHighPrecisionTargets() {
            return false;
        }

        /**
         * Init phase
         */
        init() {

        }

        /**
         * Conservatively estimate the default-block fragment uniform cost of assembled GLSL.
         *
         * Counts DECLARED uniforms, because that is what the driver measures against
         * MAX_FRAGMENT_UNIFORM_VECTORS before it decides whether the program links, and drivers
         * disagree about whether unused uniforms are eliminated first. In GLSL ES packing every
         * element of an array occupies a full vector, so `float x[13]` costs 13, not 4.
         *
         * Pure and GL-free so it can be unit tested and so it can run *before* the source is
         * handed to the driver.
         *
         * @param {string} source assembled fragment shader source
         * @return {{total: number, items: {name: string, type: string, length: number, vectors: number}[]}}
         */
        static estimateFragmentUniformVectors(source) {
            const ROWS = {
                float: 1, int: 1, uint: 1, bool: 1,
                vec2: 1, vec3: 1, vec4: 1, ivec2: 1, ivec3: 1, ivec4: 1,
                uvec2: 1, uvec3: 1, uvec4: 1, bvec2: 1, bvec3: 1, bvec4: 1,
                mat2: 2, mat3: 3, mat4: 4,
                mat2x2: 2, mat2x3: 2, mat2x4: 2,
                mat3x2: 3, mat3x3: 3, mat3x4: 3,
                mat4x2: 4, mat4x3: 4, mat4x4: 4
            };

            if (typeof source !== "string" || !source) {
                return { total: 0, items: [] };
            }

            // Array lengths are frequently written as macros or macro arithmetic
            // (COLORMAP_ARRAY_LEN_8+1, ADVANCED_SLIDER_LEN), so resolve #defines first.
            const defines = {};
            source.replace(/^[ \t]*#define[ \t]+(\w+)[ \t]+(\d+)[ \t]*$/gm, (match, key, value) => {
                defines[key] = Number.parseInt(value, 10);
                return match;
            });

            const resolveLength = (expression) => {
                let total = 0;
                for (const term of String(expression).split("+")) {
                    const trimmed = term.trim();
                    const value = /^\d+$/.test(trimmed) ? Number.parseInt(trimmed, 10) : defines[trimmed];
                    if (!Number.isInteger(value)) {
                        return 0; // unresolvable; reported as 0 rather than guessed low
                    }
                    total += value;
                }
                return total;
            };

            const declaration = /^[ \t]*uniform[ \t]+(?:(?:lowp|mediump|highp)[ \t]+)?(\w+)[ \t]+(\w+)[ \t]*(?:\[([^\]]+)\])?[ \t]*;/gm;
            const items = [];
            let total = 0;
            let match;
            while ((match = declaration.exec(source)) !== null) {
                const rows = ROWS[match[1]] !== undefined ? ROWS[match[1]] : 1; // samplers count as 1
                const length = match[3] === undefined ? 1 : Math.max(1, resolveLength(match[3]));
                const vectors = rows * length;
                items.push({ name: match[2], type: match[1], length: length, vectors: vectors });
                total += vectors;
            }
            items.sort((a, b) => b.vectors - a.vectors);
            return { total: total, items: items };
        }

        /**
         * Attach shaders and link WebGLProgram, catch errors.
         * @param {WebGLProgram} program
         * @param {WebGLRenderingContext|WebGL2RenderingContext} gl
         * @param {OpenSeadragon.FlexRenderer.WGLProgram} options build options
         * @param {function} onError
         * @param {boolean} debug
         * @return {boolean} true if program was built successfully
         */
        static _compileProgram(program, gl, options, onError, debug = false) {
            /* Napriklad gl.getProgramParameter(program, gl.LINK_STATUS) pre kind = "Program", status = "LINK", value = program */
            function ok(kind, status, value, sh) {
                if (!gl['get' + kind + 'Parameter'](value, gl[status + '_STATUS'])) {
                    $.console.error((sh || 'LINK') + ':\n' + gl['get' + kind + 'InfoLog'](value));
                    return false;
                }
                return true;
            }

            /* Attach shader to the WebGLProgram, return true if valid. */
            function useShader(gl, program, data, type) {
                let shader = gl.createShader(gl[type]);
                gl.shaderSource(shader, data);
                gl.compileShader(shader);
                gl.attachShader(program, shader);
                program[type] = shader;
                return ok('Shader', 'COMPILE', shader, type);
            }

            function numberLines(str) {
                // from https://stackoverflow.com/questions/49714971/how-to-add-line-numbers-to-beginning-of-each-line-in-string-in-javascript
                return str.split('\n').map((line, index) => `${index + 1} ${line}`).join('\n');
            }

            // Attaching shaders to WebGLProgram failed
            if (!useShader(gl, program, options.vertexShader, 'VERTEX_SHADER') ||
                !useShader(gl, program, options.fragmentShader, 'FRAGMENT_SHADER')) {
                onError("Unable to correctly build WebGL shaders.",
                    "Attaching of shaders to WebGLProgram failed. For more information, see logs in the $.console.");
                $.console.warn("VERTEX SHADER\n", numberLines( options.vertexShader ));
                $.console.warn("FRAGMENT SHADER\n", numberLines( options.fragmentShader ));
                return false;
            } else { // Shaders attached
                gl.linkProgram(program);
                if (!ok('Program', 'LINK', program)) {
                    onError("Unable to correctly build WebGL program.",
                        "Linking of WebGLProgram failed. For more information, see logs in the $.console.");
                    $.console.warn("VERTEX SHADER\n", numberLines( options.vertexShader ));
                    $.console.warn("FRAGMENT SHADER\n", numberLines( options.fragmentShader ));
                    return false;
                } else if (debug) {
                    $.console.info("VERTEX SHADER\n", numberLines( options.vertexShader ));
                    $.console.info("FRAGMENT SHADER\n", numberLines( options.fragmentShader ));
                }
                return true;
            }
        }

        /**
         * Get WebGL version of the implementation.
         * @return {String} "1.0" or "2.0"
         */
        getVersion() {
            return undefined;
        }

        sampleTexture() {
            throw("$.FlexRenderer.WebGLImplementation::sampleTexture() must be implemented!");
        }

        getTextureSize() {
            throw("$.FlexRenderer.WebGLImplementation::getTextureSize() must be implemented!");
        }

        getShaderLayerGLSLIndex() {
            throw("$.FlexRenderer.WebGLImplementation::getShaderLayerGLSLIndex() must be implemented!");
        }

        createProgram() {
            throw("$.FlexRenderer.WebGLImplementation::createProgram() must be implemented!");
        }

        loadProgram() {
            throw("$.FlexRenderer.WebGLImplementation::loadProgram() must be implemented!");
        }

        useProgram() {
            throw("$.FlexRenderer.WebGLImplementation::useProgram() must be implemented!");
        }

        /**
         * Set viewport dimensions. Parent context already applied correct viewport settings to the
         * OpenGL engine. These values are already configured, but the webgl context can react to them.
         * @param {Number} x
         * @param {Number} y
         * @param {Number} width
         * @param {Number} height
         * @param {Number} levels number of layers that are rendered, kind of 'depth' parameter, an integer
         * @param {Number} tiledImageCount number of tiled images carrying the levels
         * @instance
         * @memberof FlexRenderer
         */
        setDimensions(x, y, width, height, levels, tiledImageCount) {
            //no-op
        }

        /**
         * Set viewer background color, supports #RGBA syntax
         * @param (background)
         */
        setBackground(background) {
        }

        destroy() {
        }

        /**
         * Get supported render modes by the renderer. First should be the default.
         * Keywords with *mask* are deprecated.
         * @return {string[]}
         */
        get supportedUseModes() {
            return ["show", "blend", "clip", "mask", "clip_mask"];
        }

        /**
         * Reuse the current first-pass result and render the normal second pass into an offscreen target.
         *
         * Contract:
         * - consumes the same `renderArray` that would be passed to `renderSecondPass(...)`
         * - must not mutate renderer-owned inspector state
         * - should render the normal second-pass composition, not apply special lens logic here
         * - returns a backend-owned target object that can later be consumed by compositor logic
         *
         * @param {SPRenderPackage[]} renderArray second-pass draw packages
         * @param {Object} [options={}]
         * @param {Object|string} [options.target] backend-owned render target object or stable target key
         * @param {string} [options.targetKey] stable target key used when `target` is omitted
         * @param {number} [options.width] target width in physical pixels
         * @param {number} [options.height] target height in physical pixels
         * @param {number[]} [options.clearColor] RGBA used when `renderArray` is empty
         * @return {Object}
         */
        renderSecondPassToTexture(renderArray, options = {}) {
            throw("$.FlexRenderer.WebGLImplementation::renderSecondPassToTexture() must be implemented!");
        }

        /**
         * Ensure a reusable backend-owned color target.
         *
         * @param {object|null} target existing target slot, or null to allocate
         * @param {number} width target width in physical pixels
         * @param {number} height target height in physical pixels
         * @param {object} [options={}]
         * @return {object}
         */
        ensureColorTarget(target, width, height, options = {}) {
            throw("$.FlexRenderer.WebGLImplementation::ensureColorTarget() must be implemented!");
        }

        /**
         * Clear a backend-owned color target.
         *
         * @param {object} target
         * @param {number[]} [rgba]
         * @return {void}
         */
        clearColorTarget(target, rgba = [0, 0, 0, 0]) {
            throw("$.FlexRenderer.WebGLImplementation::clearColorTarget() must be implemented!");
        }

        /**
         * Destroy a backend-owned color target.
         *
         * @param {object|null} target
         * @return {void}
         */
        destroyColorTarget(target) {
            throw("$.FlexRenderer.WebGLImplementation::destroyColorTarget() must be implemented!");
        }

        /**
         * Copy a backend-owned color target into a presentation canvas.
         *
         * The presentation canvas size is owned by FlexRenderer#setDimensions().
         * Implementations must not resize it.
         *
         * @param {object} target
         * @param {HTMLCanvasElement} canvas
         * @returns {string} Transfer mode used.
         */
        presentColorTargetToCanvas(target, canvas) {
            throw("$.FlexRenderer.WebGLImplementation::presentColorTargetToCanvas() must be implemented!");
        }

        /**
         * Execute the backend-specific inspector compositor path for modes that cannot be expressed
         * inline in the normal second pass.
         *
         * Phase-1 contract:
         * - this hook is used only for `lens-zoom`
         * - reveal/A-B behavior must stay inside the normal second-pass program
         * - the backend should render the full second pass to an offscreen target and then run a
         *   cheap compositor over that full result
         * - no base/alternate texture semantics are part of the public contract
         *
         * The backend must read the canonical state from `renderer.getInspectorState()` and produce
         * the same visible result as the reference WebGL2 implementation for `lens-zoom`.
         *
         * @param {SPRenderPackage[]} renderArray second-pass draw packages
         * @param {Object} [options={}]
         * @param {GLint|null} [options.framebuffer]
         * @return {RenderOutput|Object}
         */
        processSecondPassWithInspector(renderArray, options = {}) {
            throw("$.FlexRenderer.WebGLImplementation::processSecondPassWithInspector() must be implemented!");
        }

        /**
         * Return sampling GLSL code (no function definition allowed) that implements blending passed by name
         * available are vec4 arguments 'fg' and 'bg'
         * e.g.:
         *   return vec4(fg.rgb * bg.a, fg.a);
         * @param {string} name one of OpenSeadragon.FlexRenderer.BLEND_MODE
         * @return {string}
         */
        getBlendingFunction(name) {
            throw("$.FlexRenderer.WebGLImplementation::blendingFunction must be implemented!");
        }

        /**
         * Prepare bitmap-like tile data as a backend-owned render resource.
         *
         * Concrete backends should override this method. The base implementation
         * returns a structured unsupported result rather than throwing, because
         * tile preparation failures are expected recoverable values.
         *
         * @param {PrepareBitmapTileOptions} options - Bitmap tile preparation options.
         * @returns {Promise<PreparedRasterTileResult>} Preparation result.
         */
        async prepareBitmapTile(options = {}) {
            return {
                ok: false,
                reason: "unsupported-data",
                error: new Error(`${this.constructor.name || "Backend"} does not support bitmap tile preparation.`)
            };
        }

        /**
         * Prepare GPU texture-set tile data as a backend-owned render resource.
         *
         * Concrete backends should override this method.
         *
         * @param {PrepareGpuTextureTileOptions} options - GPU texture-set preparation options.
         * @returns {Promise<PreparedRasterTileResult>} Preparation result.
         */
        async prepareGpuTextureTile(options = {}) {
            return {
                ok: false,
                reason: "unsupported-data",
                error: new Error(`${this.constructor.name || "Backend"} does not support GPU texture tile preparation.`)
            };
        }

        /**
         * Prepare vector mesh tile data as backend-owned render resources.
         *
         * Concrete backends should override this method.
         *
         * @param {PrepareVectorTileOptions} options - Vector tile preparation options.
         * @returns {Promise<PreparedVectorTileResult>} Preparation result.
         */
        async prepareVectorTile(options = {}) {
            return {
                ok: false,
                reason: "unsupported-data",
                error: new Error(`${this.constructor.name || "Backend"} does not support vector tile preparation.`)
            };
        }

        /**
         * Release a backend-owned prepared tile resource.
         *
         * Concrete backends should override this method when they return
         * resources from preparation methods.
         *
         * @param {*} resource - Backend-owned prepared tile resource.
         * @returns {void}
         */
        releasePreparedTileResource(resource) {
            // no-op in the abstract backend
        }
    };

    /**
     * @typedef {object} RenderOptions
     * @property {GLint|null} [framebuffer=null]
     *
     * todo: needs to differentiate first and second pass... might need to define interface for both individually
     */

    /**
     * WebGL Program instance
     * @class OpenSeadragon.FlexRenderer.WGLProgram
     */
    $.FlexRenderer.WGLProgram = class extends $.FlexRenderer.Program {

        /**
         *
         * @param context
         * @param gl {WebGLRenderingContext|WebGL2RenderingContext} Rendering program.
         * @param atlas {OpenSeadragon.FlexRenderer.TextureAtlas} Shared texture atlas.
         */
        constructor(context, gl, atlas) {
            super(context);
            /**
             *
             * @type {WebGLRenderingContext}
             */
            this.gl = gl;
            /**
             * @type {$.FlexRenderer.TextureAtlas}
             */
            this.atlas = atlas;
            this._webGLProgram = null;
            /**
             *
             * @type {string}
             */
            this.fragmentShader = "";
            /**
             *
             * @type {string}
             */
            this.vertexShader = "";
        }

        get webGLProgram() {
            if (!this._webGLProgram) {
                throw Error("Program accessed without registration - did you call this.renderer.registerProgram()?");
            }
            return this._webGLProgram;
        }

        /**
         *
         * @param shaderMap
         * @param shaderKeys
         */
        build(shaderMap, shaderKeys) {
        }

        /**
         * Create program.
         * @param width
         * @param height
         */
        created(width, height) {
        }

        /**
         * Retrieve program error message
         * @return {string|undefined} error message of the current state or undefined if OK
         */
        getValidateErrorMessage() {
            if (!this.vertexShader || !this.fragmentShader) {
                return "Program does not define vertexShader or fragmentShader shader property!";
            }
            return undefined;
        }

        /**
         * Load program. Arbitrary arguments.
         * Called ONCE per shader lifetime. Should not be called twice
         * unless requested by this.requireLoad=true call -- you should not set values
         * that are lost when webgl program is changed.
         */
        load() {
        }

        /**
         * Use program. Arbitrary arguments.
         * @param {RenderOutput} renderOutput the object passed between first and second pass
         * @param {FPRenderPackage[]|SPRenderPackage[]} renderArray
         * @param {RenderOptions|undefined} options used for now only for second pass, to specify which FBO to render to
         */
        use(renderOutput, renderArray, options) {
        }

        unload() {

        }

        /**
         * Destroy program. No arguments.
         */
        destroy() {
        }

// TODO we might want to fire only for active program and do others when really encesarry or with some delay, best at some common implementation level
        setDimensions(x, y, width, height, levels, tiledImageCount) {

        }

        /**
         * Iterate GLSL code
         */
        printN(fn, number, padding = "") {
            const output = new Array(number);
            for (let i = 0; i < number; i++) {
                output[i] = padding + fn(i);
            }
            return output.join('\n');
        }
    };

    /**
     * Texture atlas for WebGL. Shaders should be offered addImage(...) interface that returns atlas ID to
     * use in turn to access the desired image on-gpu.
     * @type {{gl: WebGL2RenderingContext, layerWidth: number|number, layerHeight: number|number, layers: number|number, padding: number|number, maxIds: number|number, internalFormat: number|0x8058, format: number|0x1908, type: number|0x1401, texture: null, new(WebGL2RenderingContext, {layerWidth?: number, layerHeight?: number, layers?: number, padding?: number, maxIds?: number, internalFormat?: number, format?: number, type?: number}=): $.FlexRenderer.TextureAtlas, prototype: TextureAtlas}}
     */
    $.FlexRenderer.TextureAtlas = class {
        /**
         * Construct the atlas, optionally using custom parameters. The atlas is
         * supposed to use layers (2d array or 3d texture) to allow growth once hitting
         * the max texture 2D dimension.
         * @param {WebGL2RenderingContext} gl
         * @param {{
         *   layerWidth?: number,
         *   layerHeight?: number,
         *   layers?: number,
         *   padding?: number,
         *   maxIds?: number,
         *   internalFormat?: number,
         *   format?: number,
         *   type?: number
         * }} [opts]
         */
        constructor(gl, opts) {
            this.gl = gl;

            this.layerWidth = (opts && opts.layerWidth) ? opts.layerWidth : 512;
            this.layerHeight = (opts && opts.layerHeight) ? opts.layerHeight : 512;
            this.layers = (opts && typeof opts.layers === 'number') ? opts.layers : 1;
            this.padding = (opts && typeof opts.padding === 'number') ? opts.padding : 1;
            this.maxIds = (opts && typeof opts.maxIds === 'number') ? opts.maxIds : 256;

            this.internalFormat = (opts && opts.internalFormat) ? opts.internalFormat : gl.RGBA8;
            this.format = (opts && opts.format) ? opts.format : gl.RGBA;
            this.type = (opts && opts.type) ? opts.type : gl.UNSIGNED_BYTE;

            this.texture = null;
        }

        /**
         * Add an image. Returns a stable textureId.
         * @param {ImageBitmap|HTMLImageElement|HTMLCanvasElement|ImageData|Uint8Array} source
         * @param {{
         *   width?: number,
         *   height?: number,
         * }} [opts]
         * @returns {number}
         */
        addImage(source, opts) {
            throw new Error('TextureAtlas2DArray.addImage: not implemented');
        }

        /**
         * Texture atlas works as a single texture unit. Bind the atlas before using it at desired texture unit.
         * @param textureUnit
         */
        bind(textureUnit) {}

        /**
         * Get WebGL Atlas shader code. This code must define the following function:
         * vec4 osd_atlas_texture(int, vec2)
         * which selects texture ID (1st arg) and returns the color at the uv position (2nd arg)
         *
         * @return {string}
         */
        getFragmentShaderDefinition() {
            throw new Error('TextureAtlas2DArray.getFragmentShaderDefinition: not implemented');
        }

        /**
         * Load the current atlas uniform locations.
         * @param {WebGLProgram} program
         */
        load(program) {
        }

        /**
         * Destroy the atlas.
         */
        destroy() {
        }
    };

    $.FlexRenderer.WebGL10 = class extends $.FlexRenderer.WebGLImplementation {
        // todo implement support
    };

})(OpenSeadragon);
